import React, { useState, useEffect, useCallback } from "react";
import {
  Typography,
  Button,
  Chip,
  Dialog,
  Grid,
  Tooltip,
  Box,
  Divider,
} from "@mui/material";
// import SelectPlanModalQuotit from "components/HealthPlan/SelectPlanModalQuotit";
import ViewDetailsModalQuotit from "components/HealthPlan/ViewDetailsModalQuotit";
import { useFirebase } from "contexts/Firebase";
import { useAuth } from "contexts/User";
import {
  query,
  collection,
  where,
  orderBy,
  doc,
  getDoc,
  updateDoc,
  setDoc,
} from "firebase/firestore";
import { useCollection } from "react-firebase-hooks/firestore";
import { FormattedNumber } from "react-intl";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
// import { useRemoteConfig } from "contexts/RemoteConfig";
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import GppGoodIcon from '@mui/icons-material/GppGood';
import { QuotitHealthPlanDetails } from "services/Interfaces";
import { QuotitPlanConverter } from "services/Firestore/Plan";

interface PremiumTier {
  employeeContribution: number;
  employerContribution: number;
  totalPremium: number;
}

interface FourTierPremium {
  employeeOnly: PremiumTier;
  employeePlusChildren: PremiumTier;
  employeePlusSpouse: PremiumTier;
  family: PremiumTier;
}

interface BenefitPlan {
  id: string;
  name: string;
  carrier: string;
  planNetworkType: string;
  individualDeductible: number;
  familyDeductible: number;
  individualOutOfPocketMaximum: number;
  familyOutOfPocketMaximum: number;
  fourTierPremium: FourTierPremium;
}

// interface QuotitBenefit {
//   enum: string;
//   inNetwork: string;
// }

// interface QuotitCarrier {
//   name: string;
//   largeLogo: string;
// }

// interface QuotitPlan {
//   id: string;
//   name: string;
//   carrier: QuotitCarrier;
//   planType: string;
//   rate: number;
//   benefits: QuotitBenefit[];
// }

// Add this interface for the plan details
interface SelectedPlanDetails {
  id: string;
  name: string;
  carrier: string;
  planType: string;
  rate: number;
  deductible: string | number;
  outOfPocketMax: string | number;
}

interface PlanOptionsJamieAIProps {
  effectiveDate: string;
  speed: number;
  finished?: Function;
  autoScroll?: Function;
  cobraEstimate: number;
  currentPlan?: BenefitPlan;
  conversationId?: string;
  sendPrompt: (message: string) => void;
  onPlanSelect?: (planDetails: SelectedPlanDetails) => void;
}

const PlanOptionsJamieAI = ({
  effectiveDate,
  speed,
  finished,
  autoScroll,
  cobraEstimate,
  currentPlan,
  conversationId,
  sendPrompt,
  onPlanSelect,
}: PlanOptionsJamieAIProps) => {
  const { user, answers } = useAuth();
  const { firestore } = useFirebase();
  const [openViewPlan, setOpenViewPlan] = useState(false);
  const [planToView, setPlanToView] = useState<any>(null);
  const [planOptions, setPlanOptions] = useState<Array<any>>([]);
  const [imageError, setImageError] = React.useState<boolean>(false);

  // Get selected benefit plan
  const [, setSelectedBenefitPlan] =
    useState<BenefitPlan | null>(null);

  // Add a function to render star ratings based on score
  const renderStars = (score: number) => {
    const rating = Math.round(score / 20); // Example: Scale 0-100 score to 0-5 stars
    const stars = [];
    for (let i = 0; i < 5; i++) {
      if (i < rating) {
        stars.push(<StarIcon key={i} sx={{ fontSize: '1.2rem', color: '#ffb400' }} />); // Gold color for filled stars
      } else {
        stars.push(<StarBorderIcon key={i} sx={{ fontSize: '1.2rem', color: 'text.disabled' }} />);
      }
    }
    return stars;
  };

  // Add a function to render different colored badges based on plan ranking
  const renderBadge = (index: number) => {
    let color = '';
    let label = '';
    
    switch(index) {
      case 0:
        color = '#FFD700'; // Gold
        label = 'Gold';
        break;
      case 1:
        color = '#C0C0C0'; // Silver
        label = 'Silver';
        break;
      case 2:
        color = '#CD7F32'; // Bronze
        label = 'Bronze';
        break;
      default:
        color = '#CD7F32'; // Default to bronze
        label = 'Bronze';
    }
    
    return (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', ml: 1, mt:1.5 }}>
        <GppGoodIcon sx={{ color: color, fontSize: '1.4rem', mr: 0.5 }} />
        <Typography component="span" variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary', mt:.5 }}>
          {label}
        </Typography>
      </Box>
    );
  };

  useEffect(() => {
    const fetchSelectedPlan = async () => {
      if (!user?.uid || !firestore) return;

      try {
        const settingsRef = doc(firestore, `users/${user.uid}/settings/chat`);
        const settingsDoc = await getDoc(settingsRef);

        if (settingsDoc.exists() && settingsDoc.data().selectedBenefitPlan) {
          const planData = settingsDoc.data().selectedBenefitPlan;
          setSelectedBenefitPlan(planData);
        } else if (settingsDoc.exists() && settingsDoc.data().selectedPlan) {
          const planId = settingsDoc.data().selectedPlan;
          console.warn("Selected benefit plan details not found in chat settings, using ID:", planId);
        }
      } catch (error) {
        console.error("Error fetching selected plan:", error);
      }
    };

    fetchSelectedPlan();
  }, [user?.uid, firestore]);

  // Determine the premium tier based on household members
  // const determinePremiumTier = (members: any): keyof FourTierPremium => {
  //   if (!members) return "employeeOnly";

  //   const memberCount = Object.keys(members).length;
  //   const hasSpouse = Object.values(members).some(
  //     (member: any) => member.relationship === "spouse",
  //   );
  //   const hasChildren = Object.values(members).some(
  //     (member: any) => member.relationship === "child",
  //   );

  //   if (memberCount === 1) return "employeeOnly";
  //   if (hasSpouse && hasChildren) return "family";
  //   if (hasSpouse) return "employeePlusSpouse";
  //   if (hasChildren) return "employeePlusChildren";
  //   return "employeeOnly";
  // };

  // const household = answers?.get("insuranceDetails.household.members");

  const [plans] = useCollection<QuotitHealthPlanDetails>(
    user?.uid
      ? query(
          collection(
            firestore,
            `users/${user.uid}/effectiveDate/${effectiveDate}/plans`,
          ).withConverter(QuotitPlanConverter),
          orderBy("insuranceType"),
          orderBy("recommendation.internalScore", "desc"),
          where("insuranceType", "!=", "ShortTerm"),
        )
      : null,
  );

  useEffect(() => {
    if (!plans?.docs) return;

    const filteredRankedPlans = plans.docs
    .map(doc => doc.data())
    .filter(plan => plan.insuranceType === 'HealthOnExchange' && plan.recommendation?.internalScore != null)
    .sort((a, b) => (b.recommendation?.internalScore || 0) - (a.recommendation?.internalScore || 0))
    .slice(0, 3);

    setPlanOptions(filteredRankedPlans);
  }, [plans]);

  const federalSubsidyAmount =
    answers?.data()?.insuranceDetails?.federalSubsidyAmount || 0;

  function getSubsidyAmount(originalPremium: number, subsidyAmount: number) {
    if (subsidyAmount > 0 && subsidyAmount < originalPremium) {
      return originalPremium - subsidyAmount;
    }
    return originalPremium;
  }

  const saveRecommendedPlansToConvo = useCallback(async (recommendedPlans: any[]) => {
    if (!user?.uid || !conversationId) return;

    try {
      const convoRef = doc(
        firestore,
        `users/${user.uid}/convos/${conversationId}`,
      );
      const convoSnapshot = await getDoc(convoRef);

      if (convoSnapshot.exists()) {
        const convoData = convoSnapshot.data();
        const messages = convoData.messages || [];

        if (messages.length > 0) {
          const lastMessage = messages[messages.length - 1];

          // Format plans data with correct value extraction
          const formattedPlans = recommendedPlans.map((plan) => {
            let deductible = "$0";
            let outOfPocketMax = "$0";
            const deductibleValue = plan.benefits?.find((b: any) => b.enum === "ACA1MedicalDrugDeductible")?.inNetwork;
            const oopValue = plan.benefits?.find((b: any) => b.enum === "ACA1EHBDrugOOPMax")?.inNetwork;

            deductible = deductibleValue ? String(deductibleValue) : "$0";
            outOfPocketMax = oopValue ? String(oopValue) : "$0";

            return {
              id: plan.id,
              name: plan.name,
              rate: plan.rate || 0,
              deductible: deductible,
              outOfPocketMax: outOfPocketMax,
            };
          });

          if (!lastMessage.data) {
            lastMessage.data = {};
          }

          lastMessage.data.recommendedPlans = formattedPlans;

          await updateDoc(convoRef, {
            messages: messages,
          });
        }
      }
    } catch (error) {
      console.error("Error saving recommended plans:", error);
    }
  }, [user?.uid, conversationId, firestore]);

  useEffect(() => {
    if (planOptions.length > 0) {
      saveRecommendedPlansToConvo(planOptions);
    }
  }, [planOptions, saveRecommendedPlansToConvo]);

  const handlePlanSelection = async (plan: any) => {
    if (!user?.uid || !conversationId) {
      console.error("User is not authenticated or conversation ID is missing");
      return;
    }

    try {
      const deductibleValue = plan.benefits?.find((b: any) => b.enum === "ACA1MedicalDrugDeductible")?.inNetwork;
      const oopValue = plan.benefits?.find((b: any) => b.enum === "ACA1EHBDrugOOPMax")?.inNetwork;

      const planDetails: SelectedPlanDetails = {
        id: plan.id,
        name: plan.name,
        carrier: plan.carrier?.name || 'N/A',
        planType: plan.planType,
        rate: plan.rate || 0,
        deductible: deductibleValue ? String(deductibleValue) : "$0",
        outOfPocketMax: oopValue ? String(oopValue) : "$0",
      };

      // Update Firestore chat settings
      await setDoc(
        doc(firestore, "users", user.uid, "settings", "chat"),
        {
          selectedPlan: plan.id,
          selectedPlanDetails: planDetails
        },
        { merge: true }
      );

      // Call the callback with the plan details
      if (onPlanSelect) {
        onPlanSelect(planDetails);
      }

      // Update conversation last message
      const convoRef = doc(firestore, `users/${user.uid}/convos/${conversationId}`);
      const convoSnapshot = await getDoc(convoRef);

      if (convoSnapshot.exists()) {
        const convoData = convoSnapshot.data();
        const messages = convoData.messages || [];

        if (messages.length > 0) {
          const lastMessage = messages[messages.length - 1];
          if (!lastMessage.data) { lastMessage.data = {}; }
          lastMessage.data.selectedPlanId = plan.id;
          lastMessage.data.selectedPlanDetails = planDetails;

          await updateDoc(convoRef, {
            messages: messages,
          });
        }
      }

      sendPrompt("I have selected a plan and I would like to enroll.");
    } catch (error) {
      console.error("Error updating selected plan:", error);
    }
  };

  // Function to handle opening the details modal
  const viewDetails = (plan: any) => {
    setPlanToView(plan);
    setOpenViewPlan(true);
  };

  return (
    <>
      <Grid container spacing={3}>
        {planOptions.map((plan, index) => {
          const planRate = plan.rate || 0;
          const effectivePremium = getSubsidyAmount(planRate, federalSubsidyAmount);
          const savings = cobraEstimate > 0 ? cobraEstimate - effectivePremium : 0;
          const deductibleValue = plan.benefits?.find((b: any) => b.enum === "ACA1MedicalDrugDeductible")?.inNetwork;
          const oopValue = plan.benefits?.find((b: any) => b.enum === "ACA1EHBDrugOOPMax")?.inNetwork;
          const score = plan.recommendation?.internalScore || 0;

          return (
            <Grid item xs={12} key={plan.id || index}>
              <Box
                sx={{
                  borderRadius: "12px",
                  overflow: "hidden",
                  boxShadow: 1,
                  backgroundColor: "#FFFFFF",
                  border: "1px solid #E0E0E0",
                }}
              >
                <Grid container>
                  <Grid item xs={12} sm={8} container sx={{ borderRight: { sm: '1px solid #E0E0E0' } }}>
                    <Grid item xs={12} sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                      <Box sx={{ width: 50, height: 50, bgcolor: '#FFFF', borderRadius: 1, mr: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {plan.carrier?.largeLogo && !imageError ? (
                          <img
                            src={plan.carrier.largeLogo}
                            alt={`${plan.carrier.name} logo`}
                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                            onError={() => setImageError(true)}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">Logo</Typography>
                        )}
                      </Box>
                      <Box>
                        <Typography variant="h6" fontWeight="bold">
                          {plan.name} {renderBadge(index)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Insurer: <Box component="span" fontWeight={800}>{plan.carrier?.name || 'N/A'}</Box>
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Divider />
                    </Grid>

                    <Grid item xs={12} container>
                      <Grid item xs={12} md={6} sx={{ p: 2 }}>
                        <Box sx={{ p: 2, bgcolor: '#f7f7f7', borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                          <Box>
                            <Typography variant="body2" color="text.secondary">Monthly premium</Typography>
                            <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                              {cobraEstimate > 0 && effectivePremium < cobraEstimate && (
                                <Typography
                                  component="span"
                                  variant="body1"
                                  sx={{
                                    textDecoration: 'line-through',
                                    color: 'text.secondary',
                                    mr: 1,
                                  }}
                                >
                                  <FormattedNumber value={cobraEstimate} currency="USD" />
                                </Typography>
                              )}
                               <FormattedNumber value={effectivePremium} currency="USD" />
                              <Tooltip title={`Base: $${planRate.toFixed(2)}${federalSubsidyAmount > 0 ? ` - Subsidy: $${federalSubsidyAmount.toFixed(2)}` : ''}`}>
                                <InfoOutlinedIcon sx={{ fontSize: '1rem', ml: 0.5, color: 'text.secondary', cursor: 'pointer' }} />
                              </Tooltip>
                            </Typography>
                             {savings > 0 && (
                              <Chip
                                label={`SAVE ${savings.toLocaleString("en-US", { style: "currency", currency: "USD" })} VS COBRA`}
                                size="small"
                                sx={{ bgcolor: '#DCF2EB', color: '#395B50', fontWeight: 500, mt: 0.5 }}
                              />
                            )}
                          </Box>
                          <Grid container spacing={1} sx={{ mt: 1 }}>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">Deductible</Typography>
                               <Typography variant="body1" fontWeight="bold">
                                {deductibleValue ? String(deductibleValue).replace('/person', '') : 'N/A'}
                               </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">Out of Pocket Max</Typography>
                              <Typography variant="body1" fontWeight="bold">
                                {oopValue ? String(oopValue).replace('/person', '') : 'N/A'}
                               </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={6} container sx={{ p: 2 }}>
                        <Grid item xs={6} sm={6} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', p: 1 }}>
                          <Typography variant="body2" color="text.secondary">Plan Type</Typography>
                          <Typography variant="body1" fontWeight="bold">{plan.planType}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={6} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', p: 1 }}>
                          <Typography variant="body2" color="text.secondary">Insurance Type</Typography>
                           <Typography variant="body1" fontWeight="bold">{plan.insuranceType === 'HealthOnExchange' ? 'On Exchange' : plan.insuranceType}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={6} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', p: 1 }}>
                          <Typography variant="body2" color="text.secondary">Score</Typography>
                          <Typography variant="h6" fontWeight="bold">{score}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={6} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', p: 1 }}>
                          <Typography variant="body2" color="text.secondary">Star Rating</Typography>
                          <Box>
                             {renderStars(score)}
                          </Box>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12} sm={4} sx={{ p: 2, backgroundColor: '#e6f4f1', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <Button
                      variant="contained"
                      onClick={() => handlePlanSelection(plan)}
                      sx={{ bgcolor: '#395B50', '&:hover': { bgcolor: '#2a433a' }, width: '100%', maxWidth: '200px' }}
                    >
                      Select
                    </Button>
                    <Button
                      variant="text"
                      onClick={() => viewDetails(plan)}
                      sx={{
                        textDecoration: "underline",
                        color: '#395B50',
                        "&:hover": {
                          textDecoration: "underline",
                          backgroundColor: 'transparent'
                        },
                        p: 0,
                        mt: 0.5,
                      }}
                    >
                      View Details
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          );
        })}
      </Grid>

      <Dialog
        open={openViewPlan}
        onClose={() => setOpenViewPlan(false)}
        fullWidth
        maxWidth="lg"
      >
        <ViewDetailsModalQuotit
          plan={planToView}
          close={() => setOpenViewPlan(false)}
        />
      </Dialog>
    </>
  );
};

export default PlanOptionsJamieAI;
